import { useMemo } from 'react';

import {
  useComputedIssues,
  useIssueListQuery,
} from '@/features/(signal)/signals/hooks';
import {
  getDateRangeBySignalDatePreset2x,
  parseSignalDatePreset,
} from '@/features/(signal)/signals/lib';
import { SIGNAL_DATE_PRESET } from '@/features/(signal)/signals/model';

type UseHomeTopSignalsOptions = {
  datePreset?: SIGNAL_DATE_PRESET;
  limit?: number;
  sortStatus?: NotNullableSortStatusT;
  filterSet?: StructuredFilter;
};

const DEFAULT_LIMIT = 10;
const DEFAULT_DATE_PRESET = SIGNAL_DATE_PRESET.PAST_7_DAYS;
const DEFAULT_SORT_STATUS: NotNullableSortStatusT = {
  sortBy: 'feedback',
  orderStatus: 'desc',
};

export const useHomeTopSignals = (options: UseHomeTopSignalsOptions = {}) => {
  const datePreset = options.datePreset ?? DEFAULT_DATE_PRESET;
  const limit = options.limit ?? DEFAULT_LIMIT;
  const sortStatus = options.sortStatus ?? DEFAULT_SORT_STATUS;
  const filterSet = options.filterSet ?? {};

  const [start, end] = getDateRangeBySignalDatePreset2x(datePreset);
  const { unit, value } = parseSignalDatePreset(datePreset);

  const {
    originIssues,
    issueAggregates,
    signals,
    aggregateFetching,
    originIssueFetching,
    signalsFetching,
  } = useIssueListQuery({
    filterParams: {
      ...filterSet,
      start_date: start.toJSON(),
      end_date: end.toJSON(),
    },
    period: unit,
  });

  const issues = useComputedIssues({
    issueAggregates,
    originIssues,
    signals,
    datePreset,
    sortStatus,
  });

  const topIssues = useMemo(() => issues.slice(0, limit), [issues, limit]);

  return {
    topIssues,
    isLoading: aggregateFetching || originIssueFetching || signalsFetching,
    period: {
      unit,
      value,
    },
  };
};
